/**
 * Cookies & local storage page. AskTogether uses very few cookies, all
 * strictly necessary or set on explicit user action. This page is the
 * detailed counterpart to the consent banner, and the canonical
 * reference for what each named storage item is for.
 *
 * Anchor `#manage` is what the consent banner links to under
 * &ldquo;Manage cookies&rdquo;.
 */
const LAST_UPDATED = "2026-05-21";

export default function CookiesPage() {
  return (
    <main className="prose">
      <p style={{ marginBottom: "0.5rem" }}>
        <a href="/join">← Back</a>
      </p>
      <h1>Cookies &amp; storage</h1>
      <p className="meta">Last updated: {LAST_UPDATED}</p>

      <p>
        AskTogether does not use cookies for advertising or analytics. The
        items below are either strictly necessary to deliver the service
        you asked for (joining a session, staying signed in to the
        dashboard) or are set in response to an explicit action you took
        in the UI (e.g. choosing a theme).
      </p>

      <h2>Cookies</h2>
      <table className="data-inventory">
        <thead>
          <tr>
            <th>Name / scope</th>
            <th>Purpose</th>
            <th>Lifetime</th>
            <th>Type</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>_oauth2_proxy</code> on{" "}
              <code>downloadserver.co.uk</code>
            </td>
            <td>
              Keeps an admin / operator / moderator signed in to the
              dashboard at{" "}
              <code>asktogether.downloadserver.co.uk</code>. Set by our
              authentication layer after a successful Google sign-in. Not
              set on the public visitor host{" "}
              <code>asktogether.co.uk</code>.
            </td>
            <td>Session — cleared on sign-out or browser close</td>
            <td>Strictly necessary</td>
          </tr>
        </tbody>
      </table>

      <h2>Browser storage</h2>
      <p>
        The visitor experience runs entirely client-side once you&apos;ve
        joined; the items below live in your browser&apos;s{" "}
        <code>sessionStorage</code> or <code>localStorage</code>, not in
        cookies sent on every request.
      </p>
      <table className="data-inventory">
        <thead>
          <tr>
            <th>Item</th>
            <th>Purpose</th>
            <th>Lifetime</th>
            <th>Type</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>asktogether.attendee_token</code> (sessionStorage)
            </td>
            <td>
              Your attendee token for the session you joined. Lets the
              board page identify you so &ldquo;Add my name&rdquo;,
              upvotes, and your own pending questions show correctly.
            </td>
            <td>Tab session — cleared when the tab closes</td>
            <td>Strictly necessary</td>
          </tr>
          <tr>
            <td>
              <code>asktogether.session</code> (sessionStorage)
            </td>
            <td>
              Cached metadata about the session you joined (id, display
              name, anonymous flag). Lets the board render the
              &ldquo;Joined as &hellip;&rdquo; pill without an extra
              round-trip.
            </td>
            <td>Tab session — cleared when the tab closes</td>
            <td>Strictly necessary</td>
          </tr>
          <tr>
            <td>
              <code>asktogether.upvoted.&lt;sid&gt;</code> (sessionStorage)
            </td>
            <td>
              Remembers which questions you&apos;ve upvoted in this
              session so the upvote chip stays filled across page
              refreshes.
            </td>
            <td>Tab session — cleared when the tab closes</td>
            <td>Strictly necessary</td>
          </tr>
          <tr>
            <td>
              <code>asktogether.theme</code> (localStorage)
            </td>
            <td>
              Your selected theme — Auto / Light / Dark — set explicitly
              from the theme toggle in the top-right.
            </td>
            <td>Persists across visits until you change or clear it</td>
            <td>Preference (set on user action)</td>
          </tr>
          <tr>
            <td>
              <code>asktogether.cookie_notice</code> (localStorage)
            </td>
            <td>
              Records that you&apos;ve seen the cookie banner so we
              don&apos;t show it on every page load.
            </td>
            <td>Persists across visits until you clear it</td>
            <td>Strictly necessary (records your choice)</td>
          </tr>
        </tbody>
      </table>

      <h2 id="manage">Managing cookies &amp; storage</h2>
      <ul>
        <li>
          <strong>Clear sessionStorage</strong> — simply close the tab.
          Everything under sessionStorage is gone.
        </li>
        <li>
          <strong>Clear localStorage</strong> — most browsers expose this
          via &ldquo;Site settings&rdquo; or &ldquo;Storage&rdquo; in
          devtools. You can also use your browser&apos;s
          &ldquo;Clear browsing data for this site&rdquo; option.
        </li>
        <li>
          <strong>Sign out of the dashboard</strong> to clear the OAuth
          session cookie.
        </li>
        <li>
          You can block all cookies and storage for this site in your
          browser settings. The dashboard will require you to sign in
          again next visit; joining a public session may not work at all
          if sessionStorage is blocked, because we need somewhere to put
          your attendee token.
        </li>
      </ul>

      <h2>Third-party cookies</h2>
      <p>
        AskTogether sets none. Signing in via Google may set cookies on{" "}
        <code>accounts.google.com</code> for the duration of the OAuth
        flow — those are governed by Google&apos;s own cookie policy.
      </p>

      <h2>Changes</h2>
      <p>
        Substantive changes update the <em>Last updated</em> date at the
        top of this page. The cookie banner re-appears if the list of
        items changes in a way that requires fresh consent.
      </p>
    </main>
  );
}
